import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";

/* ─────────────────────────────────────────────────────────
   CSS — inyectado una vez. Prefijo .rzn-  (línea SmartPay).
   Mismo look que las tabs de zonas del rediseño de Mesas:
   chips con borde, punto de color, contador y flechas de scroll.
───────────────────────────────────────────────────────── */
const RZN_CSS = `

`;

if (typeof window !== "undefined" && !document.getElementById("rzn-styles")) {
  const el = document.createElement("style");
  el.id = "rzn-styles";
  el.textContent = RZN_CSS;
  document.head.appendChild(el);
}

/* ── iconos ── */
const IcoChevL = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>);
const IcoChevR = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>);
const IcoList = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></svg>);
const IcoLayers = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5M2 12l10 5 10-5" /></svg>);
const IcoPlus = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>);

/* ── flechas de scroll horizontal ── */
const EPS = 2;
function useScrollArrows(ref) {
  const [state, setState] = useState({ showArrows: false, canPrev: false, canNext: false });

  const recheck = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const overflow = max > EPS;
    setState({
      showArrows: overflow,
      canPrev: overflow && el.scrollLeft > EPS,
      canNext: overflow && el.scrollLeft < max - EPS,
    });
  }, [ref]);

  const scrollBy = useCallback((dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.6), behavior: "smooth" });
    setTimeout(recheck, 320);
  }, [ref, recheck]);

  const scrollPrev = useCallback(() => scrollBy(-1), [scrollBy]);
  const scrollNext = useCallback(() => scrollBy(1), [scrollBy]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const raf = requestAnimationFrame(recheck);
    el.addEventListener("scroll", recheck, { passive: true });
    window.addEventListener("resize", recheck);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(recheck);
      ro.observe(el);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(recheck);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", recheck);
      window.removeEventListener("resize", recheck);
      if (ro) ro.disconnect();
    };
  }, [ref, recheck]);

  return { ...state, scrollPrev, scrollNext, recheck };
}

export default function RfmSidebar({
  sidebarOpen = false,
  onToggleSidebar = () => { },
  zones = [],
  tables = [],
  currentZone = "all",
  onSelectZone,
  statuses = [],
  onOpenModal, // (name) => void
}) {
  const countsByZone = useMemo(() => {
    const map = {};
    for (const z of zones) map[z.id] = 0;
    for (const t of tables) {
      if (t.zone && map[t.zone] !== undefined) map[t.zone] += 1;
    }
    return map;
  }, [zones, tables]);

  const mergedCount = useMemo(() => {
    return tables.filter(
      (t) => Array.isArray(t.mergedWith) && t.mergedWith.length > 0
    ).length;
  }, [tables]);

  const selectZoneAndMaybeClose = (zoneId) => {
    onSelectZone?.(zoneId);
    // Si está abierto (mobile), lo cerramos para que se vea el floor
    if (sidebarOpen) onToggleSidebar?.();
  };

  const trackRef = useRef(null);
  const { showArrows, canPrev, canNext, scrollPrev, scrollNext, recheck } = useScrollArrows(trackRef);

  // recalcular flechas cuando cambian las zonas o el conteo
  useEffect(() => {
    const t = setTimeout(recheck, 60);
    return () => clearTimeout(t);
  }, [zones, tables, recheck]);

  return (
    <div className="rzn-scope">
      <div className="rzn-wrap">
        <div className="rzn-scroller">
          <button
            type="button"
            className="rzn-arrow"
            aria-label="Zonas anteriores"
            hidden={!showArrows}
            disabled={!canPrev}
            onClick={scrollPrev}
          >
            <IcoChevL />
          </button>

          <div className="rzn-track" ref={trackRef}>
            <button
              type="button"
              className={"rzn-chip" + (currentZone === "all" ? " active" : "")}
              onClick={() => selectZoneAndMaybeClose("all")}
            >
              <span className="rzn-ico"><IcoList /></span>
              <span>Todas</span>
              <span className="rzn-count">{tables.length}</span>
            </button>

            {zones.map((z) => (
              <button
                key={z.id}
                type="button"
                className={"rzn-chip" + (currentZone === z.id ? " active" : "")}
                onClick={() => selectZoneAndMaybeClose(z.id)}
              >
                <span className="rzn-dot" style={{ background: z.color }} />
                <span>{z.name}</span>
                <span className="rzn-count">{countsByZone[z.id] ?? 0}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="rzn-arrow"
            aria-label="Más zonas"
            hidden={!showArrows}
            disabled={!canNext}
            onClick={scrollNext}
          >
            <IcoChevR />
          </button>
        </div>



        <button className="sp-tab sp-tab-add" onClick={() => onOpenModal?.("create-zone")}>
          <IcoPlus size={14} />
          <span className=" d-none d-lg-inline d-md-inline">
            Nueva zona

          </span>

        </button>
      </div>
    </div>
  );
}



{/* Estados */ }
// <div className="rfm-sidebar-section">
//   <div className="rfm-sidebar-title">
//     <span>
//       <i className="bi bi-palette" /> Estados
//     </span>
//     <button
//       type="button"
//       onClick={() => onOpenModal?.("manage-status")}
//       title="Gestionar"
//     >
//       <i className="bi bi-gear" />
//     </button>
//   </div>

//   <div className="rfm-status-legend">
//     {statuses.map((s) => (
//       <div className="rfm-status-item" key={s.id}>
//         <span
//           className="rfm-status-dot"
//           style={{ background: s.color }}
//         />
//         <span>{s.name}</span>
//       </div>
//     ))}
//   </div>
// </div>

// {/* Resumen */}
// <div className="rfm-sidebar-section">
//   <div className="rfm-sidebar-title">
//     <span>
//       <i className="bi bi-bar-chart" /> Resumen
//     </span>
//   </div>

//   <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
//     <div
//       style={{
//         display: "flex",
//         justifyContent: "space-between",
//         marginBottom: "0.4rem",
//       }}
//     >
//       <span>Total mesas:</span>
//       <strong>{tables.length}</strong>
//     </div>
//     <div style={{ display: "flex", justifyContent: "space-between" }}>
//       <span>Mesas unidas:</span>
//       <strong>{mergedCount}</strong>
//     </div>
//   </div>
// </div>